import PaletteOutlined from '@mui/icons-material/PaletteOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

export const DASHBOARD_COLOUR_PRESETS = [
  '#11A5A4',
  '#3E7CB1',
  '#4D9078',
  '#7B5EA7',
  '#C47F17',
  '#D94B4B',
  '#455A64',
  '#E06C9F'
];

const DEFAULT_PICKER_COLOUR = DASHBOARD_COLOUR_PRESETS[0];
const HEX_COLOUR = /^#[0-9A-Fa-f]{6}$/;

export const getDashboardColourPreview = (value, fallbackColour) => {
  if (typeof value === 'string' && HEX_COLOUR.test(value.trim())) {
    return value.trim().toUpperCase();
  }
  if (
    typeof fallbackColour === 'string' &&
    HEX_COLOUR.test(fallbackColour.trim())
  ) {
    return fallbackColour.trim().toUpperCase();
  }
  return DEFAULT_PICKER_COLOUR;
};

const DashboardColourPicker = ({
  allowEmpty = true,
  disabled = false,
  emptyLabel = 'Use default colour',
  error = false,
  fallbackColour = null,
  helperText,
  label,
  onChange,
  size,
  sx,
  value = ''
}) => {
  const [anchorElement, setAnchorElement] = useState(null);
  const currentValue = typeof value === 'string' ? value : '';
  const previewColour = getDashboardColourPreview(currentValue, fallbackColour);
  const paletteOpen = Boolean(anchorElement);

  const chooseColour = (colour) => {
    onChange(colour.toUpperCase());
    setAnchorElement(null);
  };

  return (
    <>
      <TextField
        disabled={disabled}
        error={error}
        fullWidth
        helperText={helperText}
        inputProps={{ maxLength: 7 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Tooltip title={`Choose ${label.toLowerCase()}`}>
                <span>
                  <IconButton
                    aria-expanded={paletteOpen}
                    aria-label={`Choose ${label.toLowerCase()}`}
                    disabled={disabled}
                    onClick={(event) => setAnchorElement(event.currentTarget)}
                    size="small"
                    sx={{ p: 0.5 }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        bgcolor: previewColour,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 0.75,
                        height: 22,
                        width: 22
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          )
        }}
        label={label}
        onChange={(event) => onChange(event.target.value)}
        placeholder={fallbackColour || DEFAULT_PICKER_COLOUR}
        size={size}
        sx={sx}
        value={currentValue}
      />

      <Popover
        anchorEl={anchorElement}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={() => setAnchorElement(null)}
        open={paletteOpen}
      >
        <Box sx={{ maxWidth: 280, p: 2 }}>
          <Stack alignItems="center" direction="row" spacing={1}>
            <PaletteOutlined color="action" fontSize="small" />
            <Typography fontWeight={700} variant="body2">
              Choose {label.toLowerCase()}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: 'repeat(4, 36px)',
              mt: 1.5
            }}
          >
            {DASHBOARD_COLOUR_PRESETS.map((colour) => (
              <Tooltip key={colour} title={colour}>
                <IconButton
                  aria-label={`Use ${colour}`}
                  aria-pressed={currentValue.toUpperCase() === colour}
                  onClick={() => chooseColour(colour)}
                  size="small"
                  sx={{
                    border: '2px solid',
                    borderColor:
                      currentValue.toUpperCase() === colour
                        ? 'text.primary'
                        : 'transparent',
                    p: 0.25
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      bgcolor: colour,
                      borderRadius: '50%',
                      height: 26,
                      width: 26
                    }}
                  />
                </IconButton>
              </Tooltip>
            ))}
          </Box>

          <Stack
            alignItems="center"
            direction="row"
            spacing={1.5}
            sx={{ mt: 2 }}
          >
            <Box
              aria-label={`Pick custom ${label.toLowerCase()}`}
              component="input"
              onChange={(event) => onChange(event.target.value.toUpperCase())}
              sx={{
                bgcolor: 'transparent',
                border: 0,
                cursor: 'pointer',
                height: 36,
                p: 0,
                width: 44
              }}
              type="color"
              value={previewColour}
            />
            <Typography color="text.secondary" variant="body2">
              Custom colour
            </Typography>
          </Stack>

          {allowEmpty && (
            <Button
              fullWidth
              onClick={() => {
                onChange('');
                setAnchorElement(null);
              }}
              size="small"
              sx={{ mt: 1.5 }}
            >
              {emptyLabel}
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default DashboardColourPicker;
